/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { EmailTemplate } from 'types/entities/EmailTemplate'

export class EmailExportUtils {
    static exportToEml = async (
        template: EmailTemplate,
        placeholders: { [key: string]: string },
        attachments: File[] = []
    ) => {
        let subject = template.subject
        let content = template.content

        Object.entries(placeholders).forEach(([key, value]) => {
            subject = subject.replaceAll(key, value || '')
            content = content.replaceAll(key, value || '')
        })

        const boundary = `----boundary_${Math.random().toString(36).substring(2)}----`
        const htmlBody = `<html><body style="font-family: sans-serif;">${content.replace(/\n/g, '<br>')}</body></html>`

        const lines: string[] = []
        lines.push(`Subject: ${subject}`)
        lines.push('MIME-Version: 1.0')
        lines.push('X-Unsent: 1')
        lines.push(`Content-Type: multipart/mixed; boundary="${boundary}"`)
        lines.push('')

        // HTML Part
        lines.push(`--${boundary}`)
        lines.push('Content-Type: text/html; charset=utf-8')
        lines.push('Content-Transfer-Encoding: 7bit')
        lines.push('')
        lines.push(htmlBody)
        lines.push('')

        // Attachments
        for (const file of attachments) {
            const base64 = await this.fileToBase64(file)
            lines.push(`--${boundary}`)
            lines.push(
                `Content-Type: ${file.type || 'application/octet-stream'}; name="${file.name}"`
            )
            lines.push('Content-Transfer-Encoding: base64')
            lines.push(
                `Content-Disposition: attachment; filename="${file.name}"`
            )
            lines.push('')
            lines.push(base64)
            lines.push('')
        }

        lines.push(`--${boundary}--`)

        const emlContent = lines.join('\r\n')
        const blob = new Blob([emlContent], { type: 'message/rfc822' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${template.name || 'template'}.eml`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
    }

    private static fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
                const result = reader.result as string
                // data:application/pdf;base64,JVBERi0... -> JVBERi0...
                resolve(result.split(',')[1])
            }
            reader.onerror = (error) => reject(error)
        })
    }
}
